'use client';

import { OpeningHoursFlyoutContent } from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/types/types";
import {
    Contact,
    SlideOutA11Y,
    SlideOutContact,
    SlideOutEntranceFee,
    SlideOutEvent,
    SlideOutEventLocation,
    SlideOutNewsletter
} from "@/app/lib/generated/graphql-types";
import {
    getA11ySlideOutContentCached,
    getApproachFlyoutContentCached,
    getContactSlideOutContentCached,
    getEntranceFeeFlyoutContentCached,
    getEventLocationsFlyoutContentCached,
    getEventsFlyoutContentCached,
    getNewsletterFlyoutContentCached,
    getOpeningHoursFlyoutContentCached
} from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/data-access/queries";

const flyoutContentPromises = new Map<string, Promise<unknown>>();

type FlyoutLoadResult<T> = { data?: T, error?: Error };

function loadCached<T>(cacheKey: string, loader: () => Promise<FlyoutLoadResult<T>>): Promise<FlyoutLoadResult<T>> {
    const cachedPromise = flyoutContentPromises.get(cacheKey);
    if (cachedPromise) return cachedPromise as Promise<FlyoutLoadResult<T>>;

    const promise = loader().catch((error) => {
        flyoutContentPromises.delete(cacheKey);
        throw error;
    });

    flyoutContentPromises.set(cacheKey, promise);
    return promise;
}

function normalizeError(error: unknown): Error {
    if (error instanceof Error) return error;
    return new Error('Unknown flyout loading error');
}

async function loadFlyoutResult<T>(
    cacheKey: string,
    loader: () => Promise<FlyoutLoadResult<T>>
): Promise<FlyoutLoadResult<T>> {
    try {
        return await loadCached(cacheKey, loader);
    } catch (error) {
        return { error: normalizeError(error) };
    }
}

export function loadOpeningHoursFlyoutContent(): Promise<FlyoutLoadResult<OpeningHoursFlyoutContent>> {
    return loadFlyoutResult('opening-hours', getOpeningHoursFlyoutContentCached);
}

export function loadEntranceFeeFlyoutContent(): Promise<FlyoutLoadResult<SlideOutEntranceFee>> {
    return loadFlyoutResult('entrance-fee', getEntranceFeeFlyoutContentCached);
}

export function loadEventsFlyoutContent(): Promise<FlyoutLoadResult<SlideOutEvent>> {
    return loadFlyoutResult('events', getEventsFlyoutContentCached);
}

export function loadEventLocationsFlyoutContent(): Promise<FlyoutLoadResult<SlideOutEventLocation>> {
    return loadFlyoutResult('event-locations', getEventLocationsFlyoutContentCached);
}

export function loadApproachFlyoutContent(): Promise<FlyoutLoadResult<Contact>> {
    return loadFlyoutResult('approach', getApproachFlyoutContentCached);
}

export function loadA11yFlyoutContent(): Promise<FlyoutLoadResult<SlideOutA11Y>> {
    return loadFlyoutResult('a11y', getA11ySlideOutContentCached);
}

export function loadNewsletterFlyoutContent(): Promise<FlyoutLoadResult<SlideOutNewsletter>> {
    return loadFlyoutResult('newsletter', getNewsletterFlyoutContentCached);
}

export function loadContactFlyoutContent(): Promise<FlyoutLoadResult<{ slideOutContact: SlideOutContact, contact: Contact }>> {
    return loadFlyoutResult('contact', getContactSlideOutContentCached);
}

let preloadAllPromise: Promise<void> | null = null;

export function preloadAllFlyoutContent(): Promise<void> {
    if (preloadAllPromise) return preloadAllPromise;

    preloadAllPromise = Promise.allSettled([
        loadOpeningHoursFlyoutContent(),
        loadEntranceFeeFlyoutContent(),
        loadEventsFlyoutContent(),
        loadEventLocationsFlyoutContent(),
        loadApproachFlyoutContent(),
        loadA11yFlyoutContent(),
        loadNewsletterFlyoutContent(),
        loadContactFlyoutContent(),
    ]).then(() => undefined);

    return preloadAllPromise;
}
